'use client';

import React, { useEffect, useRef, useState } from 'react';

import R2Image from '@/components/ui/R2Image';
import type {
  LovedayCardLocation,
  LovedayCardsSection,
} from '@/features/loveday/hooks/fetchData';

interface CardsSectionProps {
  data?: LovedayCardsSection;
}

export const CardsSection = ({
  data,
}: CardsSectionProps): React.ReactElement | null => {
  const cards = data?.components || [];
  const [active, setActive] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  if (!data || cards.length === 0) {
    return null;
  }

  const scrollToCard = (index: number): void => {
    const track = trackRef.current;
    setActive(index);
    if (track?.scrollTo) {
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
    }
  };

  return (
    <section className="w-full bg-white py-4 lg:py-5">
      <div className="content-wrapper">
        {data.heading && (
          <p className="text-h2 mb-10 text-center uppercase">{data.heading}</p>
        )}

        {/* Mobile: horizontal snap scroll */}
        <div className="md:hidden">
          <div
            ref={trackRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-6"
          >
            {cards.map((card: LovedayCardLocation, index: number) => {
              // Use IntersectionObserver instead of onScroll to update the active dot.
              return (
                <CardItem
                  key={index}
                  card={card}
                  className="w-[85vw] shrink-0 snap-start"
                  trackRef={trackRef}
                  index={index}
                  setActive={setActive}
                />
              );
            })}
          </div>
          {/* Pagination dots */}
          <div className="flex justify-center gap-2">
            {cards.map((_: LovedayCardLocation, i: number) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollToCard(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={active === i ? 'true' : undefined}
                className={`h-2 rounded-full transition-all ${active === i ? 'bg-theme-accent w-6' : 'w-2 bg-gray-300'}`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: grid */}
        <div className="hidden grid-cols-3 gap-6 md:grid lg:grid-cols-3">
          {cards.map((card: LovedayCardLocation, index: number) => (
            <CardItem key={card.title || index} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Internal sub-component for individual cards
const CardItem = ({
  card,
  className = '',
  trackRef,
  index,
  setActive,
}: {
  card: LovedayCardLocation;
  className?: string;
  trackRef?: React.RefObject<HTMLDivElement | null>;
  index?: number;
  setActive?: (i: number) => void;
}): React.ReactElement => {
  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!trackRef || index === undefined || !setActive) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setActive(index);
      },
      { root: trackRef.current, threshold: 0.5 },
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return (): void => observer.disconnect();
  }, [trackRef, index, setActive]);

  return (
    <article
      ref={cardRef}
      className={`bg-bg-secondary flex flex-col rounded-xl p-5 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg ${className}`}
    >
      <div className="relative mb-4 h-53 w-full overflow-hidden rounded-lg">
        {card?.image?.url && (
          <R2Image
            path={card.image.url}
            alt={card.title || 'Location image'}
            sizes="(max-width: 768px) 85vw, 25vw"
            className="object-cover"
          />
        )}
      </div>
      {card.heading &&
        (card.mapsUrl ? (
          <a
            href={card.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-primary font-bold uppercase underline"
          >
            {card.heading}
          </a>
        ) : (
          <p className="text-theme-primary font-bold uppercase">
            {card.heading}
          </p>
        ))}
      {card.contact && (
        <p className="text-theme-primary mt-1 text-sm font-bold">
          {card.contact}
        </p>
      )}
      {card.address && (
        <p className="text-text-main mt-4 text-sm leading-relaxed">
          {card.address}
        </p>
      )}
    </article>
  );
};
